/**
 * Pure layout logic for the organism screen (DESIGN.md §8): the breakpoint
 * resolver, the log-rail width clamp, and defensive localStorage
 * (de)serialization. Kept pure/DOM-free so it's unit-testable without a
 * browser — `OrganismScreen.ts` is the only place that touches
 * `window`/`localStorage` directly.
 */

export type LayoutZone = "three-zone" | "two-zone" | "single-column";

/** Breakpoints (DESIGN.md §8): >=1440 a full-height log rail beside a
 * brain-dominant main column; 1100-1439 the log becomes a full-width bottom
 * dock; <1100 everything stacks in one column.
 *
 * 1440px (not 1500) is deliberate: the owner's own reported-cramped
 * screenshot and this pass's hard acceptance test ("≥40 visible rows") are
 * both measured at exactly 1440x900 — a bottom dock physically cannot show
 * 40 twenty-pixel rows in a 900px-tall viewport (that needs ~800px of dock
 * body alone), only the full-height rail can. 1440x900 is also the single
 * most common laptop resolution this console is viewed at, so it gets the
 * rail rather than the dock. */
export function resolveLayoutZone(viewportWidth: number): LayoutZone {
  if (viewportWidth >= 1440) return "three-zone";
  if (viewportWidth >= 1100) return "two-zone";
  return "single-column";
}

export const DEFAULT_RAIL_WIDTH_PX = 420;
export const MIN_RAIL_WIDTH_PX = 300;

/** The rail can never eat more than half the viewport (a very narrow
 * three-zone window, e.g. exactly 1500px, would otherwise let a careless
 * drag crush the brain to nothing) nor grow unboundedly on an ultrawide
 * monitor. */
export function maxRailWidthPx(viewportWidth: number): number {
  return Math.max(MIN_RAIL_WIDTH_PX, Math.min(720, Math.round(viewportWidth * 0.5)));
}

export function clampRailWidth(px: number, viewportWidth: number): number {
  if (!Number.isFinite(px)) return DEFAULT_RAIL_WIDTH_PX;
  const max = maxRailWidthPx(viewportWidth);
  return Math.min(max, Math.max(MIN_RAIL_WIDTH_PX, Math.round(px)));
}

export interface LayoutState {
  readonly railWidthPx: number;
  readonly logDockExpanded: boolean;
  /** Per-panel collapsed flag, keyed by the panel title passed to
   * `OrganismScreen.makePanel` (stable across a session — the titles are
   * fixed strings, not user data). Absence of a key means "no explicit
   * user choice yet" — the caller then falls back to the panel's own
   * default (e.g. Life/Family auto-collapse when not substantive). */
  readonly collapsedPanels: Readonly<Record<string, boolean>>;
}

export const DEFAULT_LAYOUT_STATE: LayoutState = {
  railWidthPx: DEFAULT_RAIL_WIDTH_PX,
  logDockExpanded: true,
  collapsedPanels: {},
};

export const LAYOUT_STORAGE_KEY = "obs-layout-v1";

/** Defensive parse: a corrupt value, an old schema, or `localStorage`
 * throwing (private browsing, quota) all degrade to
 * `DEFAULT_LAYOUT_STATE` — layout persistence is a nice-to-have, never a
 * crash surface. Exported separately from the `localStorage` read so it's
 * testable with plain JS values. */
export function sanitizeLayoutState(raw: unknown): LayoutState {
  if (typeof raw !== "object" || raw === null) return DEFAULT_LAYOUT_STATE;
  const value = raw as Record<string, unknown>;

  const railWidthPx =
    typeof value["railWidthPx"] === "number" && Number.isFinite(value["railWidthPx"])
      ? value["railWidthPx"]
      : DEFAULT_LAYOUT_STATE.railWidthPx;

  const logDockExpanded =
    typeof value["logDockExpanded"] === "boolean"
      ? value["logDockExpanded"]
      : DEFAULT_LAYOUT_STATE.logDockExpanded;

  const collapsedPanels: Record<string, boolean> = {};
  const rawCollapsed = value["collapsedPanels"];
  if (typeof rawCollapsed === "object" && rawCollapsed !== null) {
    for (const [key, val] of Object.entries(rawCollapsed as Record<string, unknown>)) {
      if (typeof val === "boolean") collapsedPanels[key] = val;
    }
  }

  return { railWidthPx, logDockExpanded, collapsedPanels };
}

export function loadLayoutState(
  storage: Pick<Storage, "getItem"> = window.localStorage,
): LayoutState {
  try {
    const raw = storage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return DEFAULT_LAYOUT_STATE;
    return sanitizeLayoutState(JSON.parse(raw));
  } catch {
    return DEFAULT_LAYOUT_STATE;
  }
}

export function saveLayoutState(
  state: LayoutState,
  storage: Pick<Storage, "setItem"> = window.localStorage,
): void {
  try {
    storage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Best-effort only (private browsing / quota) — never throw from a
    // layout-persistence side effect.
  }
}
